import { z } from "zod/v4";
import { defineResource, defineEndpoint } from "@visioni/a3s-client-api";
import { NOTIFICATION_ROUTE_FEATURE } from "./constants";

/**
 * Duplicates the identical rule on `SmsContact.phone_number` in
 * `a3s-alarm-schedules`. Not imported from there: that package depends on this
 * one, so the import would be a dependency cycle. Both belong in `a3s-schema`
 * as a domain primitive.
 */
export const PhoneNumber = z
  .string()
  .regex(/^\+\d{6,15}$/, "Phone number must be in international format, e.g. +61123456789");

/**
 * Client-API resource surface for self-service notification contact info
 * (currently just a phone number for SMS). Tenant-scoped, not camera-scoped
 * (`scope: "tenant"`) — like `notification-preferences`, this always belongs
 * to the calling user (`actor` from `requireAuth()`), never a
 * path/body-supplied user id.
 */

export const notificationContactInfoResource = defineResource({
  feature: NOTIFICATION_ROUTE_FEATURE,
  resource: "notification-contact-info",
  scope: "tenant",
});

export const GetContactPhoneNumberResponse = z.object({
  phoneNumber: PhoneNumber.nullable(),
});
export type GetContactPhoneNumberResponse = z.infer<typeof GetContactPhoneNumberResponse>;

/** GET the calling user's phone number for notification delivery, or `null` if never set. */
export const getContactPhoneNumberEndpoint = defineEndpoint(notificationContactInfoResource, {
  responseSchema: GetContactPhoneNumberResponse,
});

export const SetContactPhoneNumberBody = z.object({
  phoneNumber: PhoneNumber.nullable(),
});
export type SetContactPhoneNumberBody = z.infer<typeof SetContactPhoneNumberBody>;

/** PUT the calling user's phone number for notification delivery, or clear it with `null`. */
export const setContactPhoneNumberEndpoint = defineEndpoint(notificationContactInfoResource, {
  method: "PUT",
  bodySchema: SetContactPhoneNumberBody,
  responseSchema: GetContactPhoneNumberResponse,
});
